import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search, X, Loader2, User, Building2, Hash, ArrowRight, Clock,
  LayoutDashboard, FolderKanban, BarChart3, Settings, Users2, Send,
  Globe, CalendarDays, FileText, Receipt, Briefcase, BookOpen,
  Bus, Banknote, Gift, Shield, Megaphone, Zap, Bot, Youtube,
  Mail, PlusCircle, ExternalLink, Command, Sparkles,
} from 'lucide-react';
import { crmApi, SERVICE_TYPE_LABELS } from '../services/crmApi';

// ─── Types ───────────────────────────────────────────────────────
interface SearchResult {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_name: string;
  service_type: string;
  status: string;
  created_at: string;
}

interface CommandItem {
  id: string;
  label: string;
  sublabel?: string;
  icon: React.ElementType;
  iconColor?: string;
  category: 'navigation' | 'action' | 'external' | 'recent';
  path?: string;
  url?: string;
  action?: () => void;
  keywords?: string;
}

// ─── Static Commands ─────────────────────────────────────────────
const NAV_COMMANDS: CommandItem[] = [
  // Main
  { id: 'nav-dashboard', label: 'Dashboard', sublabel: 'Przegld CRM', icon: LayoutDashboard, iconColor: 'text-blue-400', category: 'navigation', path: '/dashboard', keywords: 'start home pulpit' },
  { id: 'nav-kanban', label: 'Kanban PUP', sublabel: 'Wnioski dotacyjne', icon: FolderKanban, iconColor: 'text-indigo-400', category: 'navigation', path: '/crm', keywords: 'wnioski dotacje pup kanban' },
  { id: 'nav-executive', label: 'Executive Dashboard', sublabel: 'KPI, lejki, prognozy', icon: Sparkles, iconColor: 'text-amber-400', category: 'navigation', path: '/cockpit', keywords: 'kpi roi revenue przychody executive cockpit' },
  { id: 'nav-analytics', label: 'Analytics', sublabel: 'GA4 + YouTube', icon: BarChart3, iconColor: 'text-violet-400', category: 'navigation', path: '/analytics', keywords: 'ga4 statystyki ruch analytics' },
  { id: 'nav-reports', label: 'Raporty', sublabel: 'Eksporty i zestawienia', icon: FileText, iconColor: 'text-cyan-400', category: 'navigation', path: '/reports', keywords: 'raport eksport csv excel' },
  { id: 'nav-tasks', label: 'Zadania', sublabel: 'Lista zadan', icon: FolderKanban, iconColor: 'text-amber-400', category: 'navigation', path: '/tasks', keywords: 'zadania tasks todo lista' },
  { id: 'nav-taskboard', label: 'Tablica zadan', sublabel: 'Kanban zadan', icon: FolderKanban, iconColor: 'text-violet-400', category: 'navigation', path: '/task-board', keywords: 'tablica kanban board zadania' },
  { id: 'nav-calendar', label: 'Kalendarz', sublabel: 'Spotkania i terminy', icon: CalendarDays, iconColor: 'text-blue-400', category: 'navigation', path: '/kalendarz', keywords: 'kalendarz spotkanie meeting termin' },
  { id: 'nav-daily', label: 'Raport dzienny', sublabel: 'Podsumowanie dnia', icon: CalendarDays, iconColor: 'text-sky-400', category: 'navigation', path: '/daily-digest', keywords: 'raport dzienny daily digest' },

  // CRM / Klienci
  { id: 'nav-clients', label: 'Klienci', sublabel: 'Baza klientow', icon: Users2, iconColor: 'text-emerald-400', category: 'navigation', path: '/clients', keywords: 'klienci baza kontakty osoby' },
  { id: 'nav-companies', label: 'Firmy B2B', sublabel: 'Baza firm', icon: Building2, iconColor: 'text-indigo-400', category: 'navigation', path: '/companies', keywords: 'firmy companies b2b nip regon' },
  { id: 'nav-client360', label: 'Klient 360', sublabel: 'Pelny profil klienta', icon: Users2, iconColor: 'text-indigo-400', category: 'navigation', path: '/client-360', keywords: 'klient 360 profil szczegoly' },
  { id: 'nav-segments', label: 'Segmenty', sublabel: 'Segmentacja klientow', icon: Users2, iconColor: 'text-violet-400', category: 'navigation', path: '/segments', keywords: 'segmenty grupy klientow' },
  { id: 'nav-contractors', label: 'Kontrahenci', sublabel: 'Dostawcy i partnerzy', icon: Building2, iconColor: 'text-slate-400', category: 'navigation', path: '/contractors', keywords: 'kontrahenci dostawcy partnerzy' },
  { id: 'nav-duplicates', label: 'Duplikaty', sublabel: 'Wykrywanie duplikatow', icon: Users2, iconColor: 'text-orange-400', category: 'navigation', path: '/duplicates', keywords: 'duplikaty merge scalanie' },

  // Products
  { id: 'nav-wns', label: 'Wsparcie na Start', sublabel: 'Kanban WnS', icon: Shield, iconColor: 'text-violet-400', category: 'navigation', path: '/wsparcie', keywords: 'wns wsparcie start' },
  { id: 'nav-grants', label: 'Dotacje Rozwojowe', sublabel: 'Kanban dotacji', icon: Sparkles, iconColor: 'text-emerald-400', category: 'navigation', path: '/dotacje-rozwojowe', keywords: 'dotacje rozwojowe dev grant' },
  { id: 'nav-loans', label: 'Pozyczki Rozwojowe', sublabel: 'Kanban pozyczek', icon: Banknote, iconColor: 'text-teal-400', category: 'navigation', path: '/pozyczki-rozwojowe', keywords: 'pozyczki kredyt loan' },
  { id: 'nav-wycieczki', label: 'Wycieczki', sublabel: 'eNHa TRIP', icon: Bus, iconColor: 'text-emerald-400', category: 'navigation', path: '/wycieczki', keywords: 'wycieczki trip autobus' },
  { id: 'nav-trainings', label: 'Szkolenia', sublabel: 'KFS i kursy', icon: BookOpen, iconColor: 'text-orange-400', category: 'navigation', path: '/szkolenia', keywords: 'szkolenia kfs kursy training' },
  { id: 'nav-kfs', label: 'KFS Hub', sublabel: 'Panel KFS', icon: BookOpen, iconColor: 'text-sky-400', category: 'navigation', path: '/kfs-dashboard', keywords: 'kfs hub szkolenia dotacje' },
  { id: 'nav-turnusy', label: 'Turnusy', sublabel: 'Rehabilitacja', icon: CalendarDays, iconColor: 'text-rose-400', category: 'navigation', path: '/turnusy', keywords: 'turnusy rehab pfron' },
  { id: 'nav-hutnik', label: 'Hutnik Business', sublabel: 'Klub biznesowy', icon: Briefcase, iconColor: 'text-amber-400', category: 'navigation', path: '/hutnik-crm', keywords: 'hutnik biznes sponsor' },
  { id: 'nav-sklep', label: 'Sklep Nowohucki', sublabel: 'E-commerce', icon: BarChart3, iconColor: 'text-emerald-400', category: 'navigation', path: '/sklep', keywords: 'sklep nowohucki shop' },

  // Tools
  { id: 'nav-mailing', label: 'Mailing', sublabel: 'Kampanie email', icon: Send, iconColor: 'text-pink-400', category: 'navigation', path: '/mailing', keywords: 'mailing email kampania newsletter' },
  { id: 'nav-templates', label: 'Szablony email', sublabel: 'Edytor szablonow', icon: Mail, iconColor: 'text-slate-400', category: 'navigation', path: '/email-templates', keywords: 'szablon template email' },
  { id: 'nav-invoices', label: 'Faktury', sublabel: 'Rozliczenia', icon: Receipt, iconColor: 'text-teal-400', category: 'navigation', path: '/invoices', keywords: 'faktura invoice rozliczenie' },
  { id: 'nav-finance', label: 'Finanse', sublabel: 'Dashboard finansowy', icon: BarChart3, iconColor: 'text-emerald-400', category: 'navigation', path: '/finance', keywords: 'finanse budzet przychody wydatki' },
  { id: 'nav-commissions', label: 'Prowizje', sublabel: 'Rozliczenia prowizji', icon: BarChart3, iconColor: 'text-amber-400', category: 'navigation', path: '/commissions', keywords: 'prowizje commissions' },
  { id: 'nav-social', label: 'Social Media', sublabel: 'Publikacje FB/IG', icon: Megaphone, iconColor: 'text-pink-400', category: 'navigation', path: '/social-media', keywords: 'social facebook instagram post' },
  { id: 'nav-team', label: 'Zespol HR', sublabel: 'Pracownicy', icon: Users2, iconColor: 'text-green-400', category: 'navigation', path: '/team-members', keywords: 'zespol hr pracownik kadry' },
  { id: 'nav-notifications', label: 'Powiadomienia', icon: Megaphone, iconColor: 'text-rose-400', category: 'navigation', path: '/notifications', keywords: 'powiadomienia alerty' },
  { id: 'nav-ai', label: 'AI Agent', sublabel: 'Asystent AI', icon: Bot, iconColor: 'text-purple-400', category: 'navigation', path: '/ai-agent', keywords: 'ai chat bot asystent' },
  { id: 'nav-documents', label: 'Szablony dokumentow', sublabel: 'Generator dokumentow', icon: FileText, iconColor: 'text-blue-400', category: 'navigation', path: '/document-templates', keywords: 'dokumenty szablony generator' },
  { id: 'nav-wiki', label: 'Wiki wewnetrzna', sublabel: 'Baza wiedzy', icon: BookOpen, iconColor: 'text-indigo-400', category: 'navigation', path: '/wiki', keywords: 'wiki baza wiedzy knowledge' },
  { id: 'nav-activity', label: 'Dziennik aktywnosci', sublabel: 'Log dzialan', icon: FileText, iconColor: 'text-gray-400', category: 'navigation', path: '/activity-log', keywords: 'aktywnosc log dziennik historia' },

  // Admin
  { id: 'nav-admin', label: 'Panel Admina', sublabel: 'Ustawienia systemu', icon: Settings, iconColor: 'text-slate-400', category: 'navigation', path: '/admin/panel', keywords: 'admin panel ustawienia settings' },
  { id: 'nav-users', label: 'Uzytkownicy', sublabel: 'Konta i role', icon: Users2, iconColor: 'text-indigo-400', category: 'navigation', path: '/users', keywords: 'uzytkownicy konta role permissions' },
  { id: 'nav-websites', label: 'Strony WWW', sublabel: 'Monitoring stron', icon: Globe, iconColor: 'text-sky-400', category: 'navigation', path: '/crm/websites', keywords: 'www strony monitoring health' },
  { id: 'nav-audit', label: 'Audit Log', sublabel: 'Historia zmian', icon: FileText, iconColor: 'text-gray-400', category: 'navigation', path: '/audit', keywords: 'audit log historia zmiany' },
  { id: 'nav-settings', label: 'Ustawienia', sublabel: 'Preferencje konta', icon: Settings, iconColor: 'text-slate-400', category: 'navigation', path: '/settings', keywords: 'ustawienia preferencje konto profil' },
  { id: 'nav-monitoring', label: 'Monitoring', sublabel: 'Status systemu', icon: BarChart3, iconColor: 'text-green-400', category: 'navigation', path: '/monitoring', keywords: 'monitoring health status serwer' },
];

const ACTION_COMMANDS: CommandItem[] = [
  { id: 'act-new-app', label: 'Nowy wniosek PUP', sublabel: 'Ctrl+N', icon: PlusCircle, iconColor: 'text-green-400', category: 'action', path: '/crm?action=new', keywords: 'nowy wniosek dodaj pup' },
  { id: 'act-new-wns', label: 'Nowy wniosek WnS', icon: PlusCircle, iconColor: 'text-violet-400', category: 'action', path: '/wsparcie?action=new', keywords: 'nowy wns wsparcie dodaj' },
  { id: 'act-new-grant', label: 'Nowy wniosek dotacyjny', icon: PlusCircle, iconColor: 'text-emerald-400', category: 'action', path: '/dotacje-rozwojowe?action=new', keywords: 'nowy dotacja dodaj' },
  { id: 'act-new-loan', label: 'Nowy wniosek pozyczkowy', icon: PlusCircle, iconColor: 'text-teal-400', category: 'action', path: '/pozyczki-rozwojowe?action=new', keywords: 'nowy pozyczka dodaj' },
  { id: 'act-new-mailing', label: 'Nowa kampania mailing', icon: Send, iconColor: 'text-pink-400', category: 'action', path: '/mailing/new', keywords: 'nowy mailing kampania email' },
];

const EXTERNAL_COMMANDS: CommandItem[] = [
  { id: 'ext-portainer', label: 'Portainer', sublabel: 'panel.krakow.business', icon: ExternalLink, iconColor: 'text-cyan-400', category: 'external', url: 'https://panel.krakow.business' },
  { id: 'ext-analytics', label: 'Google Analytics', sublabel: 'analytics.google.com', icon: BarChart3, iconColor: 'text-orange-400', category: 'external', url: 'https://analytics.google.com/analytics/web/#/p424562565' },
  { id: 'ext-youtube', label: 'YouTube Studio', sublabel: 'studio.youtube.com', icon: Youtube, iconColor: 'text-red-400', category: 'external', url: 'https://studio.youtube.com' },
  { id: 'ext-admin', label: 'Google Admin', sublabel: 'admin.google.com', icon: Shield, iconColor: 'text-blue-400', category: 'external', url: 'https://admin.google.com' },
  { id: 'ext-uptime', label: 'Uptime Kuma', sublabel: 'Monitoring', icon: Zap, iconColor: 'text-green-400', category: 'external', url: 'https://uptime.fundacjanh.org' },
];

const ALL_COMMANDS = [...NAV_COMMANDS, ...ACTION_COMMANDS, ...EXTERNAL_COMMANDS];

const CATEGORY_LABELS: Record<string, string> = {
  recent: 'Ostatnie',
  action: 'Szybkie akcje',
  navigation: 'Nawigacja',
  external: 'Linki zewnetrzne',
  search: 'Wyniki wyszukiwania',
};

// Crown alias not needed — already using Sparkles directly

// ─── Recent Pages Tracker ────────────────────────────────────────
const RECENT_KEY = 'cmdk_recent';
function getRecent(): string[] {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
}
function addRecent(id: string) {
  const recent = getRecent().filter(r => r !== id);
  recent.unshift(id);
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, 8)));
}

// ─── Component ───────────────────────────────────────────────────
export const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<any>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Ctrl+K to open, Ctrl+N for new application
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        navigate('/crm?action=new');
      }
      if (e.key === 'Escape' && open) setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, navigate]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
      setSearchResults([]);
      setSelectedIdx(0);
    }
  }, [open]);

  // API search for applications
  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) { setSearchResults([]); return; }
    setLoading(true);
    try {
      const data = await crmApi.quickSearch(q);
      setSearchResults(data.results || []);
    } catch { setSearchResults([]); }
    setLoading(false);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query), 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, doSearch]);

  // Build flat list of items to show
  const items = useMemo(() => {
    const q = query.toLowerCase().trim();
    const result: Array<CommandItem | { type: 'search-result'; data: SearchResult }> = [];

    if (q.length === 0) {
      // Show recent + actions
      const recentIds = getRecent();
      const recentItems = recentIds
        .map(id => ALL_COMMANDS.find(c => c.id === id))
        .filter(Boolean) as CommandItem[];
      if (recentItems.length > 0) {
        recentItems.forEach(item => result.push({ ...item, category: 'recent' as const }));
      }
      ACTION_COMMANDS.forEach(c => result.push(c));
      NAV_COMMANDS.slice(0, 8).forEach(c => result.push(c));
    } else {
      // Filter commands by query
      const matchingCommands = ALL_COMMANDS.filter(c => {
        const searchStr = `${c.label} ${c.sublabel || ''} ${c.keywords || ''}`.toLowerCase();
        return q.split(/\s+/).every(word => searchStr.includes(word));
      });
      matchingCommands.forEach(c => result.push(c));

      // Add search results
      searchResults.forEach(r => result.push({ type: 'search-result' as const, data: r }));
    }

    return result;
  }, [query, searchResults]);

  // Scroll selected into view
  useEffect(() => {
    const el = listRef.current?.children[selectedIdx] as HTMLElement;
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIdx]);

  const handleSelect = (item: any) => {
    if (item.type === 'search-result') {
      navigate(`/applications/${item.data.id}`);
    } else if (item.url) {
      window.open(item.url, '_blank');
    } else if (item.path) {
      navigate(item.path);
    } else if (item.action) {
      item.action();
    }
    if (item.id) addRecent(item.id);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, items.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter') {
      e.preventDefault();
      // Direct #ID navigation
      const idMatch = query.trim().match(/^#(\d+)$/);
      if (idMatch) { navigate(`/applications/${idMatch[1]}`); setOpen(false); return; }
      if (items[selectedIdx]) handleSelect(items[selectedIdx]);
    }
  };

  // Group items by category for rendering
  const grouped = useMemo(() => {
    const groups: Record<string, typeof items> = {};
    for (const item of items) {
      const cat = (item as any).type === 'search-result' ? 'search' : (item as CommandItem).category;
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    }
    return groups;
  }, [items]);

  if (!open) return null;

  let flatIdx = -1;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh]">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-2xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl shadow-black/40 overflow-hidden">
        {/* Search input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-700/70">
          <Command size={18} className="text-indigo-400 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setSelectedIdx(0); }}
            onKeyDown={handleKeyDown}
            placeholder="Szukaj strony, wniosku, klienta lub wpisz akcje..."
            className="flex-1 text-sm bg-transparent text-white outline-none placeholder:text-slate-500"
            autoComplete="off"
            spellCheck={false}
          />
          {loading && <Loader2 size={16} className="animate-spin text-indigo-400" />}
          <kbd className="hidden sm:inline text-[10px] px-1.5 py-0.5 bg-slate-800 text-slate-500 rounded border border-slate-700 font-mono">ESC</kbd>
        </div>

        {/* Results list */}
        <div ref={listRef} className="max-h-[55vh] overflow-y-auto py-2">
          {Object.entries(grouped).map(([cat, catItems]) => (
            <div key={cat}>
              <div className="px-5 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                {CATEGORY_LABELS[cat] || cat}
              </div>
              {catItems.map((item) => {
                flatIdx++;
                const idx = flatIdx;
                const isSearchResult = (item as any).type === 'search-result';

                if (isSearchResult) {
                  const r = (item as any).data as SearchResult;
                  return (
                    <button key={`sr-${r.id}`} type="button"
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setSelectedIdx(idx)}
                      className={`w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors ${
                        idx === selectedIdx ? 'bg-indigo-600/20' : 'hover:bg-slate-800/60'}`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                        {r.company_name ? <Building2 size={14} className="text-amber-400" /> : <User size={14} className="text-blue-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white truncate">{r.first_name} {r.last_name}</span>
                          <span className="text-[10px] text-slate-600"><Hash size={8} className="inline" />{r.id}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-slate-500">
                          {r.email && <span className="truncate max-w-[160px]">{r.email}</span>}
                          {r.company_name && <span className="truncate max-w-[120px]">{r.company_name}</span>}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">
                          {SERVICE_TYPE_LABELS[r.service_type] || r.service_type}
                        </span>
                        <span className="text-[10px] text-slate-600 capitalize">{r.status?.replace(/_/g, ' ')}</span>
                      </div>
                      <ArrowRight size={14} className="text-slate-600 flex-shrink-0" />
                    </button>
                  );
                }

                const cmd = item as CommandItem;
                const Icon = cmd.icon;
                return (
                  <button key={cmd.id} type="button"
                    onClick={() => handleSelect(cmd)}
                    onMouseEnter={() => setSelectedIdx(idx)}
                    className={`w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors ${
                      idx === selectedIdx ? 'bg-indigo-600/20' : 'hover:bg-slate-800/60'}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                      <Icon size={15} className={cmd.iconColor || 'text-slate-400'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-white">{cmd.label}</span>
                      {cmd.sublabel && <span className="text-[11px] text-slate-500 ml-2">{cmd.sublabel}</span>}
                    </div>
                    {cmd.category === 'recent' && <Clock size={12} className="text-slate-600 flex-shrink-0" />}
                    {cmd.category === 'external' && <ExternalLink size={12} className="text-slate-600 flex-shrink-0" />}
                    {cmd.category === 'action' && <Zap size={12} className="text-amber-500 flex-shrink-0" />}
                    <ArrowRight size={14} className="text-slate-700 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          ))}

          {query.length >= 2 && items.length === 0 && !loading && (
            <div className="px-5 py-10 text-center">
              <Search size={24} className="mx-auto text-slate-600 mb-2" />
              <p className="text-sm text-slate-500">Brak wynikow dla "{query}"</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 border-t border-slate-700/70 bg-slate-900/80 flex items-center justify-between text-[10px] text-slate-600">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <Command size={10} />
              <span>+K szukaj</span>
            </div>
            <div className="flex items-center gap-1">
              <Command size={10} />
              <span>+N nowy wniosek</span>
            </div>
          </div>
          <div className="flex gap-4">
            <span><kbd className="font-mono bg-slate-800 px-1 rounded text-slate-500">#123</kbd> wniosek</span>
            <span><kbd className="font-mono bg-slate-800 px-1 rounded text-slate-500">&uarr;&darr;</kbd> nawiguj</span>
            <span><kbd className="font-mono bg-slate-800 px-1 rounded text-slate-500">Enter</kbd> otwórz</span>
          </div>
        </div>
      </div>
    </div>
  );
};
